<template>
  <v-dialog
    v-model="showHistory"
    persistent
    content-class="overflow-hidden">
    <v-card
      height="850"
      class="history-modal-card d-flex flex-column">
      <div
        class="d-flex align-center justify-space-between white--text pt-4 fill-width px-6"
        style="height: 60px;">
        <div
          class="d-flex align-center">
          <span
            class="base-color--text base-title font-weight-bold invoice-parameter-text-xl">
            {{ $t('historyModal.title') }}
          </span>
        </div>
        <v-icon
          size="26"
          color="base-color"
          @click="close">
          mdi mdi-close
        </v-icon>
      </div>

      <section
        class="history-modal-content fill-width pa-6 d-flex flex-column">
        <InfiniteScroll
          class="history-modal-table elevation-2"
          enable-column-resize
          fit-resizable-columns-to-wrapper
          :loadMore="() => {}"
          :resizable-columns="historyResizableColumns"
          :min-column-width="72"
          :headers="resolvedHeaders"
          :items="historyTableItems"
          :hasMore="false"
          :loading="false">
          <template v-slot:body="{ items }">
            <tbody>
              <template v-if="items.length">
                <template v-for="(item, itemIndex) in items">
                  <template v-for="(revision, revisionIndex) in itemRevisions(item)">
                    <tr
                      v-for="(change, changeIndex) in revisionChanges(revision)"
                      :key="bodyRowKey(itemIndex, revisionIndex, changeIndex)">
                      <td
                        v-if="changeIndex === 0"
                        :rowspan="revisionRowSpan(revision)"
                        class="col-history-timestamp">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="history-date-cell"
                              v-bind="attrs"
                              v-on="on">
                              <span class="history-date-cell__date">
                                {{ timestampParts(revision.timestamp).date }}
                              </span>
                              <span
                                v-if="timestampParts(revision.timestamp).time"
                                class="history-date-cell__time">
                                {{ timestampParts(revision.timestamp).time }}
                              </span>
                            </div>
                          </template>
                          <span>{{ displayCellText(revision.timestamp) }}</span>
                        </v-tooltip>
                      </td>
                      <td
                        v-if="changeIndex === 0"
                        :rowspan="revisionRowSpan(revision)"
                        class="col-history-revtype">
                        <OneLineCell v-bind="oneLineAttrs" :text="revision.revType" />
                      </td>
                      <td
                        v-if="changeIndex === 0"
                        :rowspan="revisionRowSpan(revision)"
                        class="col-history-username">
                        <OneLineCell
                          v-bind="oneLineAttrs"
                          :text="revision.userName"
                          :tooltipText="historyUserTooltip(revision)" />
                      </td>
                      <template v-if="changeIndex === 0">
                        <td
                          v-for="column in resolvedIdentityColumns"
                          :key="`${bodyRowKey(itemIndex, revisionIndex, changeIndex)}-${column.value}`"
                          :rowspan="revisionRowSpan(revision)"
                          :class="column.cellClass || column.class">
                          <OneLineCell v-bind="oneLineAttrs" :text="item[column.value]" />
                        </td>
                      </template>
                      <td class="col-history-attribute">
                        <OneLineCell v-bind="oneLineAttrs" :text="change.attributeName" />
                      </td>
                      <td class="col-history-oldvalue">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="history-multiline-cell"
                              v-bind="attrs"
                              v-on="on">
                              {{ displayCellText(change.oldValue) }}
                            </div>
                          </template>
                          <span>{{ displayCellText(change.oldValue) }}</span>
                        </v-tooltip>
                      </td>
                      <td class="col-history-newvalue">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <div
                              class="history-multiline-cell"
                              v-bind="attrs"
                              v-on="on">
                              {{ displayCellText(change.newValue) }}
                            </div>
                          </template>
                          <span>{{ displayCellText(change.newValue) }}</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </template>
                </template>
              </template>
              <tr v-else>
                <td
                  :colspan="resolvedHeaders.length"
                  class="py-6 text-center admission-map-text-base grey--text">
                  {{ $t('historyModal.noData') }}
                </td>
              </tr>
            </tbody>
          </template>
        </InfiniteScroll>
      </section>
    </v-card>
  </v-dialog>
</template>

<script>
import InfiniteScroll from '@/components/Default/InfiniteScroll.vue';
import OneLineCell from '@/components/Default/OneLineCell.vue';

export default {
  name: 'HistoryModal',
  components: {
    InfiniteScroll,
    OneLineCell,
  },
  data: () => ({
    showHistory: false,
    oneLineAttrs: { defaultNoValue: '—' },
  }),
  props: {
    headers: {
      type: Array,
      default: null,
    },
    identityColumns: {
      type: Array,
      default: null,
    },
    history: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  computed: {
    historyTableItems() {
      return (this.history || []).map((item) => {
        const firstRevision = this.getFirstRevision(item);

        return {
          ...item,
          timestamp: this.parseTimestampSortValue(firstRevision ? firstRevision.timestamp : null),
          revType: this.normalizeSortText(firstRevision ? firstRevision.revType : null),
          userName: this.normalizeSortText(firstRevision ? firstRevision.userName : null),
        };
      });
    },
    historyResizableColumns() {
      return this.resolvedHeaders
        .map((header) => header && header.value)
        .filter((value) => value !== undefined && value !== null && value !== '');
    },
    resolvedIdentityColumns() {
      if (this.identityColumns && this.identityColumns.length > 0) return this.identityColumns;
      return [
        {
          text: this.$t('historyModal.headers.carrierName'),
          value: 'carrierName',
          sortable: false,
          width: 110,
          class: 'col-history-carrier',
          cellClass: 'col-history-carrier',
          minWidth: 110,
        },
        {
          text: this.$t('historyModal.headers.contractName'),
          value: 'contractName',
          sortable: false,
          width: 140,
          class: 'col-history-contract',
          cellClass: 'col-history-contract',
          minWidth: 140,
        },
        {
          text: this.$t('historyModal.headers.patientName'),
          value: 'patientName',
          sortable: false,
          width: 110,
          class: 'col-history-patientname',
          cellClass: 'col-history-patientname',
          minWidth: 110,
        },
      ];
    },
    resolvedHeaders() {
      if (this.headers && this.headers.length > 0) return this.headers;
      return [
        {
          text: this.$t('historyModal.headers.timestamp'),
          value: 'timestamp',
          sortable: true,
          width: 108,
          class: 'col-history-timestamp',
          cellClass: 'col-history-timestamp',
          minWidth: 108,
          style: { width: '108px' },
        },
        {
          text: this.$t('historyModal.headers.revType'),
          value: 'revType',
          sortable: true,
          width: 96,
          class: 'col-history-revtype',
          cellClass: 'col-history-revtype',
          minWidth: 96,
        },
        {
          text: this.$t('historyModal.headers.userName'),
          value: 'userName',
          sortable: true,
          width: 128,
          class: 'col-history-username',
          cellClass: 'col-history-username',
          minWidth: 128,
        },
        ...this.resolvedIdentityColumns,
        {
          text: this.$t('historyModal.headers.attributeName'),
          value: 'attributeName',
          sortable: false,
          width: 130,
          class: 'col-history-attribute',
          cellClass: 'col-history-attribute',
          minWidth: 130,
        },
        {
          text: this.$t('historyModal.headers.oldValue'),
          value: 'oldValue',
          sortable: false,
          width: 180,
          class: 'col-history-oldvalue',
          cellClass: 'col-history-oldvalue',
          minWidth: 140,
          maxWidth: 240,
          style: { width: '180px' },
        },
        {
          text: this.$t('historyModal.headers.newValue'),
          value: 'newValue',
          sortable: false,
          width: 180,
          class: 'col-history-newvalue',
          cellClass: 'col-history-newvalue',
          minWidth: 140,
          maxWidth: 240,
          style: { width: '180px' },
        },
      ];
    },
  },
  methods: {
    open() {
      this.showHistory = true;
    },
    close() {
      this.showHistory = false;
      this.$emit('closed');
    },
    bodyRowKey(itemIndex, revisionIndex, changeIndex) {
      return `history-row-${itemIndex}-${revisionIndex}-${changeIndex}`;
    },
    getFirstRevision(item) {
      if (!item || !Array.isArray(item.revisions) || !item.revisions.length) return null;
      return item.revisions[0];
    },
    normalizeSortText(value) {
      if (value === null || value === undefined) return '';
      return String(value).toLowerCase();
    },
    parseTimestampSortValue(value) {
      if (!value) return Number.NEGATIVE_INFINITY;

      const match = String(value)
        .trim()
        .match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2}))?$/);

      if (!match) return Number.NEGATIVE_INFINITY;

      const [, day, month, year, hour = '00', minute = '00'] = match;
      return Number(`${year}${month}${day}${hour}${minute}`);
    },
    displayCellText(value) {
      if (value === null || value === undefined || value === '') {
        return this.oneLineAttrs.defaultNoValue;
      }

      return String(value);
    },
    historyUserTooltip(revision) {
      const userName = this.displayCellText(revision?.userName);
      const userEmail = this.displayCellText(revision?.userEmail);

      if (
        userName !== this.oneLineAttrs.defaultNoValue
        && userEmail !== this.oneLineAttrs.defaultNoValue
        && userName !== userEmail
      ) {
        return `${userName} (${userEmail})`;
      }

      return userName !== this.oneLineAttrs.defaultNoValue ? userName : userEmail;
    },
    timestampParts(value) {
      const text = this.displayCellText(value);
      if (text === this.oneLineAttrs.defaultNoValue) {
        return { date: text, time: '' };
      }

      const [date, time = ''] = text.split(/\s+/, 2);

      return {
        date: date || this.oneLineAttrs.defaultNoValue,
        time,
      };
    },
    itemRevisions(item) {
      if (!item || !Array.isArray(item.revisions) || !item.revisions.length) {
        return [{
          revType: null,
          timestamp: null,
          userName: null,
          userEmail: null,
          changes: [],
        }];
      }

      return item.revisions;
    },
    revisionChanges(revision) {
      if (!revision || !Array.isArray(revision.changes) || !revision.changes.length) {
        return [{ attributeName: null, oldValue: null, newValue: null }];
      }

      return revision.changes;
    },
    revisionRowSpan(revision) {
      return this.revisionChanges(revision).length;
    },
  },
};
</script>
<style lang="scss">
@import "@/shared/styles/historyModal/default.scss";
</style>
